// ────────────────────────────────────────────────────────────────────
// The rate strip — five cells in one bordered card, and the candidate ledger
// under it (design_handoff/README.md:170-176).
//
// The handoff's requirement, in its own words: "Each cell carries a one-line
// explanation of where the number came from." That is the whole point of this
// file, and of the sub-project. The founder's ruling on 2026-08-27 is the same
// instruction from the other side — the system COMPUTES AND EXPLAINS, IT DOES
// NOT DECIDE — so the losing candidates stay on screen beside the winner with
// their own sentences, and nothing here quietly picks one.
//
// NOTHING IS COMPUTED HERE. Every number arrives from
// `GET /api/assets/{id}/integrity`. In particular:
//   • which rate governs is `rates.governed_by`, decided server-side under
//     TOL = 0.0005. This file never compares `lt` to `st`. Two rates equal to
//     the last decimal a gauge produces would otherwise be separated by float
//     noise and the asset reported as accelerating when it is flat
//     (README.md:320).
//   • the due date is `due.in_force.due`, already floored to a month
//     server-side — a rounded month puts an asset a month past its issued
//     report (README.md:321, audit item 1).
//   • `basis` is printed VERBATIM. It is written as a finished sentence
//     (server/integrity/rules.py:214-229); re-wording it here would be this
//     screen inventing provenance.
//   • the four rate cells' own sentences are `record.rate_bases`, written by
//     `rules.rate_bases()` beside the candidate ones. They were composed here
//     until the record carried them, which put "where the governing rate came
//     from" in TypeScript while "where the interval came from" stayed in
//     Python — two authorities on one question, and nothing failing when they
//     drifted. They also quote the GOVERNING zone's readings, which this file
//     never had: `record.inspections` carries the asset-wide minimum per date,
//     a different part of the vessel on an asset whose worst component is not
//     the one holding its thinnest reading.
// ────────────────────────────────────────────────────────────────────
import { dueInForceIndex } from "./candidates";
import { interval as fmtInterval, monthYear, originLabel, rateMmYr, years } from "./format";
import type { AssetIntegrity } from "./types";

/** Remaining life below this many years is shown in the danger colour
 *  (design_handoff/README.md:175). A display threshold, not a rule: it changes
 *  no number and no date. */
const DANGER_YEARS = 5;

interface CellSpec {
  id: string;
  label: string;
  value: string;
  /** Small pill beside the value — currently only GOVERNED BY. */
  tag?: string;
  basis: string;
  tint?: string;
  colour: string;
  muted?: boolean;
}

function Cell({ spec, first }: { spec: CellSpec; first: boolean }): JSX.Element {
  return (
    <div data-testid={`rate-cell-${spec.id}`}
         style={{
           flex: "1 1 188px", minWidth: 188, padding: "14px 16px",
           background: spec.tint ?? "transparent",
           borderLeft: first ? undefined : "1px solid var(--line-2)",
         }}>
      <div className="font-mono"
           style={{ fontSize: 9.5, letterSpacing: ".1em", color: "var(--ink-4)" }}>
        {spec.label}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 5 }}>
        <div data-testid={`rate-value-${spec.id}`}
             data-muted={spec.muted === true ? "true" : "false"}
             className="font-mono"
             style={{ fontSize: 16, fontWeight: 600, color: spec.colour }}>
          {spec.value}
        </div>
        {spec.tag && (
          <span data-testid={`rate-tag-${spec.id}`} className="font-mono"
                style={{
                  fontSize: 9, letterSpacing: ".08em", color: "var(--ink-3)",
                  background: "var(--line-2)", padding: "2px 5px", borderRadius: 3,
                }}>
            {spec.tag}
          </span>
        )}
      </div>
      <p data-testid={`rate-basis-${spec.id}`}
         style={{ fontSize: 10.5, lineHeight: 1.5, color: "var(--ink-3)", marginTop: 7 }}>
        {spec.basis}
      </p>
    </div>
  );
}

export function RateStrip({ record }: { record: AssetIntegrity }): JSX.Element {
  const { rates, due, interval, no_rate_reason, rate_bases: basis } = record;
  const onHold = no_rate_reason === "below_minimum";
  const rl = rates.remaining_life_years;

  // Which rate is NOT the governing one. Read from the server's verdict, never
  // re-derived: EQUAL and NONE mute neither, because in neither case is there
  // a loser to mute.
  const mutedLt = rates.governed_by === "SHORT-TERM";
  const mutedSt = rates.governed_by === "LONG-TERM";
  const dim = "var(--ink-3)";

  const cells: CellSpec[] = [
    {
      id: "long_term", label: "LONG-TERM RATE", value: rateMmYr(rates.lt),
      basis: basis.long_term, muted: mutedLt, colour: mutedLt ? dim : "var(--ink-1)",
    },
    {
      id: "short_term", label: "SHORT-TERM RATE", value: rateMmYr(rates.st),
      basis: basis.short_term, muted: mutedSt, colour: mutedSt ? dim : "var(--ink-1)",
    },
    {
      id: "governing", label: "GOVERNING RATE", value: rateMmYr(rates.governing),
      tag: rates.governed_by === "NONE" ? undefined : rates.governed_by,
      basis: basis.governing, tint: "var(--surface-warm)",
      // Accent as TEXT reads --accent-deep in this app; --accent is kept for
      // fills, borders and bars (styles/HARDCODED-COLOURS.md, round 6 ruling).
      colour: "var(--accent-deep)",
    },
    {
      id: "remaining_life", label: "REMAINING LIFE",
      value: rl == null ? "none" : years(rl),
      basis: basis.remaining_life,
      colour: rl != null && rl < DANGER_YEARS ? "var(--err)" : "var(--ink-1)",
    },
    {
      id: "next_inspection", label: "NEXT INSPECTION",
      value: onHold ? "ON HOLD" : monthYear(due.in_force?.due),
      // The winning candidate's own sentence, verbatim — this cell needs no
      // fifth entry in `rate_bases` because the date already carries one. On a
      // below-minimum asset the server has appended `below_minimum_note` to it,
      // so the shortfall is stated here without this file re-wording anything.
      basis: due.in_force?.basis ?? due.why, tint: "var(--surface-warm-2)",
      colour: onHold ? "var(--err)" : "var(--ink-1)",
    },
  ];

  return (
    <section data-testid="rate-strip"
             style={{
               border: "1px solid var(--hairline)", background: "var(--paper-2)",
               borderRadius: 6, overflow: "hidden", marginTop: 16,
             }}>
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        {cells.map((c, i) => <Cell key={c.id} spec={c} first={i === 0} />)}
      </div>

      {!onHold && interval.in_force?.months != null && (
        <div className="font-mono"
             style={{
               padding: "6px 16px", borderTop: "1px solid var(--line-2)",
               fontSize: 10, letterSpacing: ".05em", color: "var(--ink-3)",
               background: "var(--surface-2)",
             }}>
          INTERVAL IN FORCE · {fmtInterval(interval.in_force.months)}
          {" · "}{originLabel(interval.in_force.origin)}
        </div>
      )}

      <CandidateLedger record={record} />
    </section>
  );
}

/** Every candidate, winner and losers alike.
 *
 *  The handoff heads the client's equivalent panel "WHY THIS DATE, AND NOT THE
 *  NOMINAL ONE", and this is that panel for staff. The losers are here on
 *  purpose: a due date nobody can see the alternatives to is a number to be
 *  believed rather than argued with, and the profile figure silently vanishing
 *  from the screen because it did not win is the exact bug the candidate model
 *  was built to stop. */
function CandidateLedger({ record }: { record: AssetIntegrity }): JSX.Element {
  const { candidates } = record.due;
  // WHICH ROW IS THE WINNER, by value. This was `c === in_force`, which is a
  // reference comparison against a JSON payload: `in_force` and the candidate
  // it names arrive as two separate objects, so it answered false on every
  // real record and the panel marked nothing at all. See ./candidates.
  const inForceAt = dueInForceIndex(record.due);
  if (candidates.length === 0) {
    return (
      <div style={{ padding: "12px 16px", borderTop: "1px solid var(--line-2)" }}>
        <p style={{ fontSize: 10.5, color: "var(--ink-3)" }}>{record.due.why}</p>
      </div>
    );
  }
  return (
    <div style={{ borderTop: "1px solid var(--line-2)", background: "var(--surface-2)" }}>
      <div className="font-mono"
           style={{
             padding: "10px 16px 6px", fontSize: 9.5, letterSpacing: ".1em",
             color: "var(--accent-deep)",
           }}>
        WHY THIS DATE, AND NOT THE NOMINAL ONE
      </div>
      {record.due.in_force != null && inForceAt < 0 && (
        // The record names a governing date that matches no candidate listed.
        // The server forbids this -- in_force is always one of candidates -- so
        // it means that contract has broken. The failure it would otherwise
        // wear is precisely the one this panel was just repaired for: every row
        // unmarked, reading as "none governs". Say it rather than show it.
        <p data-testid="in-force-unmatched"
           style={{ padding: "8px 16px", fontSize: 10.5, color: "var(--err)" }}>
          The record names {monthYear(record.due.in_force.due)} as the date in
          force, but none of the candidates below matches it.
        </p>
      )}
      {candidates.map((c, i) => {
        const inForce = i === inForceAt;
        return (
          <div key={`${c.origin}-${i}`} data-testid={`candidate-${c.origin}`}
               data-in-force={inForce ? "true" : "false"}
               style={{
                 display: "flex", gap: 12, alignItems: "flex-start",
                 padding: "8px 16px", borderTop: "1px solid var(--line-3)",
                 background: inForce ? "var(--surface-warm-3)" : "transparent",
                 boxShadow: inForce ? "inset 3px 0 0 var(--accent-2)" : undefined,
               }}>
            <div style={{ width: 108, flexShrink: 0 }}>
              <div className="font-mono"
                   style={{ fontSize: 9.5, letterSpacing: ".08em", color: "var(--ink-4)" }}>
                {originLabel(c.origin)}
              </div>
              <div className="font-mono"
                   style={{
                     fontSize: 12.5, marginTop: 2,
                     color: inForce ? "var(--accent-deep)" : "var(--ink-2)",
                   }}>
                {monthYear(c.due)}
              </div>
            </div>
            <p style={{ fontSize: 10.5, lineHeight: 1.55, color: "var(--ink-3)", flex: 1 }}>
              {/* Verbatim. rules.py wrote it to be printed. */}
              {c.basis}
            </p>
            <div style={{ width: 78, flexShrink: 0, textAlign: "right" }}>
              {inForce && (
                <div className="font-mono"
                     style={{ fontSize: 9, letterSpacing: ".08em", color: "var(--accent-deep)" }}>
                  IN FORCE
                </div>
              )}
              {c.inferred && (
                <div className="font-mono"
                     style={{ fontSize: 9, letterSpacing: ".08em", color: "var(--warn)", marginTop: 2 }}>
                  INFERRED
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
